import type { BookingStatus } from '@/types/domain/booking';
import type {
  BookingListSection,
  ProfileBookingDto,
  ProfileBookingsCountersDto,
  ProfileBookingsResponseDto,
} from '@/types/dto/booking.dto';
import type { ProfileBooking, ProfileBookingService } from '@/types/profile';

export type ProfileBookingDisplayState = 'cancelled' | 'completed' | 'confirmed' | 'pending';

function normalizeText(value?: string | null) {
  const normalizedValue = value?.trim();

  return normalizedValue ? normalizedValue : null;
}

function normalizeDate(value?: string | null) {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return '';
  }

  return normalizedValue.slice(0, 10);
}

function extractDateTimeParts(value?: string | null) {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return { date: '', time: '' };
  }

  const [datePart = '', timePart = ''] = normalizedValue.split('T');

  return {
    date: datePart.slice(0, 10),
    time: timePart.slice(0, 5),
  };
}

function differenceInMinutes(startTime?: string | null, endTime?: string | null) {
  if (!startTime || !endTime) {
    return null;
  }

  const startTimestamp = new Date(startTime).getTime();
  const endTimestamp = new Date(endTime).getTime();

  if (Number.isNaN(startTimestamp) || Number.isNaN(endTimestamp) || endTimestamp <= startTimestamp) {
    return null;
  }

  return Math.round((endTimestamp - startTimestamp) / (1000 * 60));
}

function normalizeServices(booking: ProfileBookingDto): ProfileBookingService[] {
  const sourceServices = booking.bookingServices ?? booking.services ?? [];

  return sourceServices.flatMap((service) => {
    const serviceId = normalizeText(service.id ?? service.serviceId ?? service.service?.id);
    const serviceName = normalizeText(service.name ?? service.serviceName ?? service.service?.name);
    const servicePrice =
      typeof service.price === 'number'
        ? service.price
        : typeof service.servicePrice === 'number'
          ? service.servicePrice
          : typeof service.service?.price === 'number'
            ? service.service.price
            : null;

    if (!serviceId || !serviceName) {
      return [];
    }

    return [
      {
        id: serviceId,
        name: serviceName,
        price: servicePrice,
      },
    ];
  });
}

function getBookingTimestamp(booking: ProfileBooking) {
  const bookingDateTime = new Date(`${booking.date}T${booking.time || '00:00'}:00`);

  if (Number.isNaN(bookingDateTime.getTime())) {
    return null;
  }

  return bookingDateTime.getTime();
}

export function normalizeProfileBooking(booking?: ProfileBookingDto | null): ProfileBooking | null {
  if (!booking) {
    return null;
  }

  const normalizedId = normalizeText(booking.id);

  if (!normalizedId) {
    return null;
  }

  const startDateTime = extractDateTimeParts(booking.startTime);
  const services = normalizeServices(booking);
  const totalPrice =
    typeof booking.totalPrice === 'number'
      ? booking.totalPrice
      : services.reduce((sum, service) => sum + (service.price ?? 0), 0) || null;

  return {
    companyId: normalizeText(booking.companyId ?? booking.company?.id),
    companyName: normalizeText(booking.companyName ?? booking.company?.name) ?? 'Local agendado',
    companyPhoto: normalizeText(booking.companyPhoto ?? booking.company?.profilePhoto ?? booking.company?.coverPhoto),
    companySlug: normalizeText(booking.companySlug ?? booking.company?.slug),
    createdAt: normalizeText(booking.createdAt) ?? new Date().toISOString(),
    date: normalizeDate(booking.date) || startDateTime.date,
    id: normalizedId,
    professionalName: normalizeText(booking.professionalName ?? booking.professional?.name) ?? 'Profissional',
    services,
    status: normalizeText(booking.status) as BookingStatus | string | null,
    time: normalizeText(booking.time) ?? startDateTime.time,
    totalDuration:
      typeof booking.totalDuration === 'number'
        ? booking.totalDuration
        : differenceInMinutes(booking.startTime, booking.endTime),
    totalPrice,
  };
}

export function sortProfileBookings(bookings: ProfileBooking[], section: BookingListSection) {
  return bookings.toSorted((left, right) => {
    const leftTime = getBookingTimestamp(left) ?? (section === 'upcoming' ? Number.MAX_SAFE_INTEGER : 0);
    const rightTime = getBookingTimestamp(right) ?? (section === 'upcoming' ? Number.MAX_SAFE_INTEGER : 0);

    return section === 'upcoming' ? leftTime - rightTime : rightTime - leftTime;
  });
}

export function normalizeProfileBookingsResponse(response?: ProfileBookingsResponseDto | null) {
  const counters: ProfileBookingsCountersDto = {
    history: response?.counters?.history ?? 0,
    upcoming: response?.counters?.upcoming ?? 0,
  };
  const bookings = (response?.data ?? []).flatMap((booking) => {
    const normalizedBooking = normalizeProfileBooking(booking);

    return normalizedBooking ? [normalizedBooking] : [];
  });

  return {
    bookings,
    counters,
  };
}

export function getProfileBookingDisplayState(booking: Pick<ProfileBooking, 'status'>): ProfileBookingDisplayState {
  switch (booking.status) {
    case 'CANCELLEDBYCUSTOMER':
    case 'CANCELLEDBYPROFESSIONAL':
      return 'cancelled';
    case 'COMPLETED':
      return 'completed';
    case 'CONFIRMED':
    case 'STARTED':
      return 'confirmed';
    default:
      return 'pending';
  }
}

export function getProfileBookingStatusLabel(booking: Pick<ProfileBooking, 'status'>) {
  switch (getProfileBookingDisplayState(booking)) {
    case 'cancelled':
      return 'Cancelado';
    case 'completed':
      return 'Concluído';
    case 'confirmed':
      return 'Confirmado';
    default:
      return 'Aguardando confirmação';
  }
}

export function canConfirmProfileBooking(booking: Pick<ProfileBooking, 'status'>) {
  return booking.status === 'BOOKED' || booking.status === 'PENDING';
}

export function canCancelProfileBooking(booking: Pick<ProfileBooking, 'status'>) {
  return booking.status === 'BOOKED' || booking.status === 'CONFIRMED' || booking.status === 'PENDING';
}

function isFutureProfileBooking(booking: Pick<ProfileBooking, 'date' | 'time'>, now: Date) {
  const bookingDateTime = new Date(`${booking.date}T${booking.time || '00:00'}:00`);

  if (Number.isNaN(bookingDateTime.getTime())) {
    return false;
  }

  return bookingDateTime.getTime() > now.getTime();
}

export function canRescheduleProfileBooking(
  booking: Pick<ProfileBooking, 'date' | 'status' | 'time'>,
  now = new Date(),
) {
  return canCancelProfileBooking(booking) && isFutureProfileBooking(booking, now);
}
